import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { Member } from '@/features/backlog/types';
import MemberAvatar from '@/components/member-avatar';
import { Popover } from '@/components/ui/Popover';
import { initials } from '@/components/ui/initials';
import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { MS, MS_ICON } from './ms';
import ThemeToggle from '@/components/theme-toggle';

interface Props {
  name: string;
  onRename: (name: string) => void;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomFit: () => void;
  presence: string[];
  connected: boolean;
  /** Member id we're currently following (their viewport), or null. */
  followId?: string | null;
  onFollow: (memberId: string) => void;
  onStopFollow: () => void;
  /** Bring a peer (member id) — or everyone ("all") — to our view. */
  onBring: (target: string) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Capture + upload a preview thumbnail of the whole board (shown in the listing + wiki embeds). */
  onCapture?: () => void;
  capturing?: boolean;
  /** The comments flyout, rendered just left of the presence avatars. */
  comments?: ReactNode;
}

const EyeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const TargetIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </svg>
);

/** A live-presence avatar stack that doubles as the "walk through together" roster: follow a colleague's
 *  viewport, or bring them (or everyone) to yours. Following eases YOUR camera to THEIRS (pan + zoom), not
 *  their mouse. */
function Presence({ ids, followId, onFollow, onStopFollow, onBring }: Pick<Props, 'followId' | 'onFollow' | 'onStopFollow' | 'onBring'> & { ids: string[] }) {
  const { members } = useAppData();
  const { me } = useIdentity();
  const meId = me?.id;
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const others = useMemo(() => {
    const seen = new Set<string>();
    return ids
      .filter((id) => id !== meId && !seen.has(id) && (seen.add(id), true))
      .map((id) => byId.get(id))
      .filter((m): m is Member => !!m);
  }, [ids, byId, meId]);

  const stack = [meId ? byId.get(meId) : undefined, ...others].filter((m): m is Member => !!m);
  if (stack.length === 0) return null;

  return (
    <Popover
      align="right"
      panelClassName="min-w-[252px]"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          title="People on this whiteboard"
          className={cn('flex items-center rounded-full p-0.5 transition-colors hover:bg-layer', open && 'bg-layer')}
        >
          <span className="flex -space-x-2">
            {stack.slice(0, 6).map((m) => (
              <span
                key={m.id}
                title={m.name}
                className={cn('flex h-7 w-7 items-center justify-center rounded-full border-2 text-[10px] font-semibold text-white', followId === m.id ? 'border-brand' : 'border-ink')}
                style={{ background: m.color ?? '#868e96' }}
              >
                {initials(m.name)}
              </span>
            ))}
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="min-w-[244px]">
          <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">On this whiteboard</div>
          {others.length > 0 ? (
            <button
              type="button"
              onClick={() => { onBring('all'); close(); }}
              className="mb-1 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] font-medium text-brand transition-colors hover:bg-layer"
            >
              <TargetIcon className="shrink-0" /> Bring everyone to my view
            </button>
          ) : null}
          <div className="mb-1 h-px bg-line" />
          <ul>
            {me ? (
              <li className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
                <MemberAvatar member={me} size={26} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">
                  {me.name}
                  <span className="ml-1.5 text-[11px] font-normal text-fg-faint">You</span>
                </span>
                <span className="h-2 w-2 shrink-0 rounded-full bg-ok" title="Online" />
              </li>
            ) : null}
            {others.length === 0 ? (
              <li className="px-2 py-2 text-center text-[12px] text-fg-faint">No one else is here right now.</li>
            ) : (
              others.map((m) => (
                <li key={m.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <MemberAvatar member={m} size={26} />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">{m.name}</span>
                  {followId === m.id ? (
                    <button
                      type="button"
                      onClick={() => onStopFollow()}
                      title="Stop following"
                      className="flex shrink-0 items-center gap-1 rounded-md bg-brand/15 px-2 py-1 text-[11.5px] font-semibold text-brand transition-colors hover:bg-brand/25"
                    >
                      <EyeIcon className="h-3.5 w-3.5" /> Following
                      <MS icon={MS_ICON.close} size={13} />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { onFollow(m.id); close(); }}
                        title={`Follow ${m.name}'s view`}
                        className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
                      >
                        <EyeIcon className="h-3.5 w-3.5" /> Follow
                      </button>
                      <button
                        type="button"
                        onClick={() => { onBring(m.id); }}
                        title={`Bring ${m.name} to my view`}
                        className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11.5px] font-medium text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
                      >
                        <TargetIcon className="h-3.5 w-3.5" /> Bring
                      </button>
                    </>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </Popover>
  );
}

const iconBtn = 'flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-layer hover:text-fg disabled:opacity-40 disabled:hover:bg-transparent';

export default function TopBar(p: Props) {
  const [name, setName] = useState(p.name);
  useEffect(() => setName(p.name), [p.name]);
  const { members } = useAppData();
  const followedName = p.followId ? members.find((m) => m.id === p.followId)?.name : undefined;

  return (
    <header data-wb-overlay onPointerDown={(e) => e.stopPropagation()} className="pointer-events-none absolute inset-x-0 top-0 z-30 flex h-14 items-center justify-between gap-3 px-3">
      <div className={cn(GLASS, 'pointer-events-auto relative flex items-center gap-2 rounded-2xl px-2 py-1.5')}>
        <Link to="/whiteboards" title="All whiteboards" className={iconBtn}>
          <MS icon={MS_ICON.arrow_back} />
        </Link>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name !== p.name && p.onRename(name.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          className="w-44 bg-transparent px-1 text-[14px] font-semibold text-fg-strong outline-none focus:rounded focus:bg-surface-inset"
        />
        <span className={cn('h-1.5 w-1.5 rounded-full', p.connected ? 'bg-emerald-500' : 'bg-fg-ghost')} title={p.connected ? 'Live' : 'Offline'} />
      </div>

      <div className="pointer-events-auto flex items-center gap-1.5">
        {/* Following indicator — a persistent pill so you always know you're locked to a colleague's view. */}
        {p.followId ? (
          <button
            type="button"
            onClick={p.onStopFollow}
            title="Stop following"
            className={cn(GLASS, 'flex items-center gap-1.5 rounded-2xl px-2.5 py-1.5 text-[12.5px] font-medium text-brand transition-colors hover:text-fg')}
          >
            <EyeIcon className="h-4 w-4" />
            <span className="max-w-[120px] truncate">Following {followedName ?? 'colleague'}</span>
            <MS icon={MS_ICON.close} size={15} />
          </button>
        ) : null}
        {p.comments ? <div className={cn(GLASS, 'relative flex items-center rounded-2xl px-1 py-1.5')}>{p.comments}</div> : null}
        <div className={cn(GLASS, 'relative flex items-center gap-1 rounded-2xl px-1.5 py-1.5')}>
          <Presence ids={p.presence} followId={p.followId} onFollow={p.onFollow} onStopFollow={p.onStopFollow} onBring={p.onBring} />
        </div>
        <div className={cn(GLASS, 'relative flex items-center gap-0.5 rounded-2xl px-1.5 py-1.5')}>
          <button className={iconBtn} onClick={p.onUndo} disabled={!p.canUndo} title="Undo (⌘Z)">
            <MS icon={MS_ICON.undo} />
          </button>
          <button className={iconBtn} onClick={p.onRedo} disabled={!p.canRedo} title="Redo (⌘⇧Z)">
            <MS icon={MS_ICON.redo} />
          </button>
          <span className="mx-0.5 h-5 w-px bg-line" />
          <button className={iconBtn} onClick={p.onZoomOut} title="Zoom out">
            <MS icon={MS_ICON.remove} size={20} />
          </button>
          <button className="min-w-[3rem] rounded-lg px-1 text-center text-[12px] tabular-nums text-fg-muted hover:bg-layer" onClick={p.onZoomReset} title="Reset zoom">
            {Math.round(p.zoom * 100)}%
          </button>
          <button className={iconBtn} onClick={p.onZoomIn} title="Zoom in">
            <MS icon={MS_ICON.add} size={20} />
          </button>
          <button className="rounded-lg px-2 text-[12px] text-fg-muted hover:bg-layer" onClick={p.onZoomFit} title="Zoom to fit">Fit</button>
          <span className="mx-0.5 h-5 w-px bg-line" />
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
